/* =============================================================================
 * Delta Clothing — unified B2B add-to-cart (single gate)
 *
 * Every path (PDP depot, quick view, collection card, Shopify.addItem,
 * product-form fetch) must go through here so:
 *   - Dash DraftOrder is always used (never parallel Shopify cart)
 *   - Color / Size / Warehouse line properties are collected from the
 *     correct DOM subtree (modal ≠ whole page)
 *   - S&S matrix products block adds without a color + size
 *   - Customers see an inline error instead of silent wrong pricing
 *
 * Load before b2b-product-form-bridge.js and b2b-cart-shopify-bridge.js.
 * ============================================================================= */
(function () {
  if (!window.B2B) return;
  if (window.DLC && window.DLC.cartAdd) return;

  var B2B = window.B2B;
  var DLC = window.DLC = window.DLC || {};

  function trim(s) {
    return String(s || '').trim();
  }

  /** Nearest product UI root for scoped DOM queries. */
  function findRoot(anchor) {
    if (!anchor || !anchor.closest) return document;
    return (
      anchor.closest('quick-add-modal') ||
      anchor.closest('.b2b-depot') ||
      anchor.closest('.card-wrapper') ||
      anchor.closest('product-form') ||
      anchor.closest('product-info') ||
      anchor.closest('.product') ||
      anchor.closest('section[id^="MainProduct-"]') ||
      document
    );
  }

  function requiresMatrixProps(root) {
    if (!root || !root.querySelector) return false;
    return !!root.querySelector('.b2b-depot, [data-dlc-ss-matrix="1"]');
  }

  /**
   * Read Color / Variant / Warehouse from a product subtree (not document-wide).
   * @param {Element|Document} root
   * @returns {Record<string, string>}
   */
  function readProperties(root) {
    root = root || document;
    var props = {};

    try {
      root.querySelectorAll('input[name^="properties["], textarea[name^="properties["]').forEach(function (el) {
        var m = el.name && el.name.match(/^properties\[(.+)\]$/);
        if (m && trim(el.value)) props[m[1]] = trim(el.value);
      });
    } catch (e) {}

    var swatch =
      root.querySelector('.b2b-depot .color-swatch.selected') ||
      root.querySelector('.color-swatch.selected') ||
      root.querySelector('.swatch__input--color:checked') ||
      root.querySelector('[data-color][aria-checked="true"]');
    if (swatch) {
      var color = swatch.getAttribute('data-color') || swatch.getAttribute('data-value') || swatch.value;
      if (color) props.Color = trim(color);
    }

    var selectedColorEl = root.querySelector('[data-selected-color]');
    if (selectedColorEl && trim(selectedColorEl.textContent)) {
      props.Color = trim(selectedColorEl.textContent);
    }

    var sizeEl =
      root.querySelector('select[name*="ize"]') ||
      root.querySelector('input[name*="ize"]:checked') ||
      root.querySelector('[data-size][aria-checked="true"]');
    if (sizeEl) {
      var size = sizeEl.getAttribute('data-size') || sizeEl.value;
      if (sizeEl.tagName === 'SELECT' && sizeEl.selectedOptions && sizeEl.selectedOptions[0]) {
        size = size || sizeEl.selectedOptions[0].value;
      }
      if (size) props.Variant = trim(size);
    }

    var whEl = root.querySelector('.b2b-depot [data-warehouse][data-active="true"]');
    if (whEl) {
      var wh = whEl.getAttribute('data-warehouse');
      if (wh) props.Warehouse = trim(wh);
    }

    var idField = root.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
    if (idField) {
      var opt = idField.tagName === 'SELECT' && idField.selectedOptions ? idField.selectedOptions[0] : null;
      var label = opt ? opt.textContent : '';
      if (label && label.indexOf('/') !== -1) {
        var parts = label.split('/').map(trim).filter(Boolean);
        if (parts.length >= 2) {
          if (!props.Color) props.Color = parts[0];
          if (!props.Variant) props.Variant = parts[parts.length - 1];
        }
      }
      if (!props.Variant && opt && opt.dataset && opt.dataset.size) {
        props.Variant = trim(opt.dataset.size);
      }
    }

    return props;
  }

  function mergeProps(base, extra) {
    var out = Object.assign({}, base || {});
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        if (extra[k] !== undefined && extra[k] !== null && trim(extra[k]) !== '') {
          out[k] = trim(extra[k]);
        }
      });
    }
    return out;
  }

  /**
   * @returns {{ ok: boolean, message?: string }}
   */
  function validate(root, props) {
    if (!requiresMatrixProps(root)) {
      return { ok: true };
    }
    if (!trim(props.Color)) {
      return {
        ok: false,
        message: 'Please select a color before adding to cart.',
      };
    }
    if (!trim(props.Variant)) {
      return {
        ok: false,
        message: 'Please select a size before adding to cart.',
      };
    }
    return { ok: true };
  }

  function showError(message, root) {
    message = trim(message) || 'Could not add to cart. Please try again.';
    root = root || document;

    var depotMsg = root.querySelector && root.querySelector('[data-depot-msg]');
    if (depotMsg) {
      depotMsg.textContent = message;
      depotMsg.classList.add('b2b-depot__msg--error');
      return;
    }

    var wrap =
      root.querySelector && root.querySelector('.product-form__error-message-wrapper');
    var msgEl = wrap && wrap.querySelector('.product-form__error-message');
    if (wrap && msgEl) {
      wrap.hidden = false;
      msgEl.textContent = message;
      return;
    }

    if (!document.getElementById('dlc-cart-add-toast')) {
      var css =
        '#dlc-cart-add-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);' +
        'max-width:min(480px,92vw);padding:14px 18px;background:#111;color:#fff;border-radius:8px;' +
        'box-shadow:0 12px 40px rgba(0,0,0,.25);z-index:100000;font:inherit;font-size:14px;line-height:1.45}';
      var s = document.createElement('style');
      s.textContent = css;
      document.head.appendChild(s);
      var t = document.createElement('div');
      t.id = 'dlc-cart-add-toast';
      t.setAttribute('role', 'alert');
      document.body.appendChild(t);
    }
    var toast = document.getElementById('dlc-cart-add-toast');
    toast.textContent = message;
    toast.style.display = 'block';
    clearTimeout(toast._dlcHide);
    toast._dlcHide = setTimeout(function () {
      toast.style.display = 'none';
    }, 6000);
  }

  function clearError(root) {
    root = root || document;
    var depotMsg = root.querySelector && root.querySelector('[data-depot-msg]');
    if (depotMsg) depotMsg.classList.remove('b2b-depot__msg--error');
    var wrap = root.querySelector && root.querySelector('.product-form__error-message-wrapper');
    if (wrap) wrap.hidden = true;
  }

  function notifyCartChanged(resp) {
    try {
      if (typeof window.setBagCountFromResponse === 'function') {
        window.setBagCountFromResponse(resp || {});
      }
    } catch (e) {}
    try {
      window.dispatchEvent(new CustomEvent('dlc:cart-changed', { detail: resp }));
    } catch (e) {}
    try {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (e) {}
  }

  /**
   * Authoritative add — posts to /coupon with server-side pricing.
   * @param {{ variantId: string|number, quantity?: number, properties?: object, root?: Element, onSuccess?: Function, onError?: Function, button?: Element }} opts
   */
  function postAdd(opts) {
    opts = opts || {};
    var cust = B2B.getCustomerGid();
    if (!cust) {
      window.location.href = '/account/login';
      return Promise.resolve(null);
    }

    var root = findRoot(opts.root || opts.button || document);
    var props = mergeProps(readProperties(root), opts.properties);
    var check = validate(root, props);
    if (!check.ok) {
      showError(check.message, root);
      if (typeof opts.onError === 'function') opts.onError(check);
      return Promise.resolve(null);
    }

    clearError(root);
    var variantNum = String(opts.variantId || '').replace(/\D/g, '');
    if (!variantNum) {
      showError('Missing product variant.', root);
      return Promise.resolve(null);
    }

    var qty = parseInt(opts.quantity, 10);
    if (isNaN(qty) || qty <= 0) qty = 1;

    var btn = opts.button;
    if (btn) {
      btn.setAttribute('data-loading', 'true');
      btn.classList.add('loading');
      btn.disabled = true;
    }

    var payload = {
      customer_id: cust,
      items: [{
        variant_id: 'gid://shopify/ProductVariant/' + variantNum,
        quantity: qty,
        price: 0,
        properties: props,
      }],
    };

    return fetch(B2B.endpoints.coupon, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(function (r) {
        return r.json().catch(function () {
          return { error: 'Invalid response from server' };
        });
      })
      .then(function (resp) {
        if (btn) {
          btn.removeAttribute('data-loading');
          btn.classList.remove('loading');
          btn.disabled = false;
        }
        if (resp && resp.error) {
          showError(resp.message || resp.error || 'Item could not be added.', root);
          if (typeof opts.onError === 'function') opts.onError(resp);
          return resp;
        }
        notifyCartChanged(resp);
        if (typeof opts.onSuccess === 'function') opts.onSuccess(resp);
        return resp;
      })
      .catch(function (err) {
        if (btn) {
          btn.removeAttribute('data-loading');
          btn.classList.remove('loading');
          btn.disabled = false;
        }
        console.error('[dlc] cart add failed', err);
        showError('Network error — please try again.', root);
        if (typeof opts.onError === 'function') opts.onError(err);
        return null;
      });
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return /\/cart\/add(\b|\?|$)/.test(action);
  }

  /** Block unsafe collection-card one-click add (no color picker). */
  function guardUnsafeCardForms() {
    document.querySelectorAll('.card__add-to-cart form[action*="/cart/add"]').forEach(function (form) {
      var card = form.closest('.card-wrapper');
      if (!card || card.querySelector('.b2b-depot')) return;
      if (card.querySelector('quick-add-modal, modal-opener')) {
        form.addEventListener(
          'submit',
          function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();
            showError('Open Quick View, select color and size, then add to cart.', card);
            var opener = card.querySelector('modal-opener button, modal-opener a');
            if (opener) opener.click();
          },
          true,
        );
      }
    });
  }

  /** Capture-phase submit — beats product-form.js fetch to /cart/add. */
  function onDocumentSubmit(e) {
    var form = e.target;
    if (!isCartAddForm(form)) return;
    if (!B2B.getCustomerGid()) {
      e.preventDefault();
      window.location.href = '/account/login';
      return;
    }
    e.preventDefault();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    var fd = new FormData(form);
    var variantId = fd.get('id');
    var qty = fd.get('quantity');
    var props = {};
    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && trim(value)) props[m[1]] = trim(value);
    });

    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    postAdd({
      variantId: variantId,
      quantity: qty,
      properties: props,
      root: form,
      button: btn,
      onSuccess: function () {
        var modal = form.closest('quick-add-modal');
        if (modal && typeof modal.hide === 'function') {
          modal.hide(true);
        }
      },
    });
  }

  /** Belt-and-suspenders: neuter product-form's native fetch when logged in. */
  function patchProductForm() {
    var Ctor = customElements.get('product-form');
    if (!Ctor || Ctor.prototype.__dlcPatched) return !!Ctor;
    var orig = Ctor.prototype.onSubmitHandler;
    Ctor.prototype.onSubmitHandler = function (evt) {
      if (!B2B.getCustomerGid()) return orig.call(this, evt);
      var form = this.form;
      if (!form || !isCartAddForm(form)) return orig.call(this, evt);
      evt.preventDefault();
      if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      var fd = new FormData(form);
      var self = this;
      postAdd({
        variantId: fd.get('id'),
        quantity: fd.get('quantity'),
        properties: {},
        root: this,
        button: this.submitButton,
        onSuccess: function (resp) {
          self.handleErrorMessage(false);
          var quickAddModal = self.closest('quick-add-modal');
          if (quickAddModal && self.cart) {
            document.body.addEventListener(
              'modalClosed',
              function () {
                try {
                  self.cart.renderContents(resp);
                } catch (e) {}
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else if (self.cart) {
            try {
              self.cart.renderContents(resp);
            } catch (e) {}
          }
        },
        onError: function (err) {
          if (err && err.message) self.handleErrorMessage(err.message);
        },
      });
    };
    Ctor.prototype.__dlcPatched = true;
    return true;
  }

  /** Block fetch() to Shopify cart/add for logged-in B2B buyers. */
  function patchFetch() {
    if (window.__dlc_fetch_patched || !window.fetch) return;
    window.__dlc_fetch_patched = true;
    var orig = window.fetch;
    window.fetch = function (input, init) {
      if (B2B.getCustomerGid()) {
        var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        if (/\/cart\/add(\.js)?(\?|$)/i.test(url)) {
          console.warn('[dlc] Blocked native /cart/add fetch — B2B draft cart only');
          return Promise.resolve({
            ok: false,
            status: 400,
            json: function () {
              return Promise.resolve({
                status: 400,
                description: 'Use Quick View or the product page matrix to add items.',
              });
            },
          });
        }
      }
      return orig.apply(this, arguments);
    };
  }

  DLC.cartAdd = {
    findRoot: findRoot,
    readProperties: readProperties,
    mergeProps: mergeProps,
    requiresMatrixProps: requiresMatrixProps,
    validate: validate,
    showError: showError,
    clearError: clearError,
    postAdd: postAdd,
    isCartAddForm: isCartAddForm,
  };

  document.addEventListener('submit', onDocumentSubmit, true);
  guardUnsafeCardForms();
  patchFetch();
  if (!patchProductForm()) {
    customElements.whenDefined('product-form').then(patchProductForm);
  }
  document.addEventListener('shopify:section:load', function () {
    guardUnsafeCardForms();
  });
})();
